import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@core-ui/Components/ui/table';
import { Badge } from '@core-ui/Components/ui/badge';
import { cn } from '@core-ui-lib/utils';

import { EmptyState } from '@ui/shared/emptyState';

import type { DialingContact } from '../types/wizard.types';
import { ContactsTableSkeleton } from '@features/contacts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@core-ui/Components/ui/tooltip.tsx';

interface SessionContactsTableProps {
  contacts: DialingContact[];
  isLoading?: boolean;
}

type CallStatus = 'pending' | 'called' | 'dialing' | 'voicemail' | 'transferred' | 'answered' | 'completed' | 'failed' | 'cut_off';

const statusColors: Record<CallStatus, string> = {
  transferred: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  called: 'bg-amber-500/10 text-amber-700 border-amber-500/30 dark:text-amber-300',
  dialing: 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  answered: 'bg-blue-500/10 text-blue-700 border-blue-500/30 dark:text-blue-300',
  voicemail: 'bg-violet-500/10 text-violet-700 border-violet-500/30 dark:text-violet-300',
  completed: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 dark:text-emerald-300',
  cut_off: 'bg-orange-500/10 text-orange-700 border-orange-500/30 dark:text-orange-300',
  failed: 'bg-red-500/10 text-red-700 border-red-500/30 dark:text-red-300',
  pending: 'bg-slate-500/10 text-slate-600 border-slate-500/30 dark:text-slate-400',
};

const getStatusBadge = (status?: string): { className: string; label: CallStatus } => {
  const normalized = (status ?? 'pending').toLowerCase() as CallStatus;
  const validStatus = Object.keys(statusColors).includes(normalized) ? normalized : 'pending';

  return {
    className: statusColors[validStatus],
    label: validStatus,
  };
};

const NotesTooltip = ({   
  t,
  notes,
  notesAgent,
  notesSDR,
}: {
  t: (key: string) => string;
  notes?: string;
  notesAgent?: string;
  notesSDR?: string;
}): React.JSX.Element => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="space-y-1 cursor-pointer">
            {notes != null && notes !== '' && (<div className="truncate">{t('wizard.dashboard.table.notes')}: {notes}</div>)}
            {notesAgent != null && notesAgent !== '' && (<div className="truncate">{t('wizard.dashboard.table.agentNotes')}: {notesAgent}</div>)}
            {notesSDR != null && notesSDR !== '' && (<div className="truncate">{t('wizard.dashboard.table.sdrNotes')}: {notesSDR}</div>)}
          </div>
        </TooltipTrigger>

        <TooltipContent className=" w-fit max-w-[400px] space-y-1 bg-sky-200 text-black p-6 break-all">
          {notes != null && notes !== '' && (<div><strong>{t('wizard.dashboard.table.notes')}:</strong> {notes}</div>)}
          {notesAgent != null && notesAgent !== '' && (<div><strong>{t('wizard.dashboard.table.agentNotes')}:</strong> {notesAgent}</div>)}
          {notesSDR != null && notesSDR !== '' && (<div><strong>{t('wizard.dashboard.table.sdrNotes')}:</strong> {notesSDR}</div>)}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const formatNotes = (
  t: (key: string) => string,
  notes?: string,
  notesAgent?: string,
  notesSDR?: string
): React.JSX.Element => {
  return (
    <NotesTooltip 
      t={t} 
      notes={notes}
      notesAgent={notesAgent}
      notesSDR={notesSDR}
    />
  );
};


export default function SessionContactsTable ({
  contacts,
  isLoading = false,
}: SessionContactsTableProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="w-full max-h-140 overflow-auto rounded-md border border-border bg-card">
      <Table className="table-fixed">
        <TableHeader className="bg-muted/60">
          <TableRow className="border-b border-border/60">
            <TableHead className="w-[15%] min-w-[120px]">{t('wizard.dashboard.table.name')}</TableHead>
            <TableHead className="w-[15%] min-w-[120px]">{t('wizard.dashboard.table.company')}</TableHead>
            <TableHead className="w-[18%] min-w-[140px]">{t('wizard.dashboard.table.position')}</TableHead>
            <TableHead className="w-[14%] min-w-[130px]">{t('wizard.dashboard.table.phone')}</TableHead>
            <TableHead className="w-[25%] min-w-[150px]">{t('wizard.dashboard.table.notes')}</TableHead>
            <TableHead className="w-[13%] min-w-[110px] text-right">{t('wizard.dashboard.table.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <ContactsTableSkeleton rows={3} />
          ) : contacts.length === 0 ? (
            <tr>
              <td colSpan={6} className="py-10">
                <EmptyState message={t('wizard.dashboard.noContacts')} />
              </td>
            </tr>
          ) : (
            contacts.map((contact, index) => {
              const statusBadge = getStatusBadge(contact.finalStatus);
              const rowKey = contact.id !== '' ? contact.id : `${contact.phone ?? ''}-${index}`;

              return (
                <TableRow
                  key={rowKey}
                  className={cn(
                    'border-b border-border hover:bg-muted/30',
                    statusBadge.label === 'transferred' && 'bg-emerald-500/5',
                    statusBadge.label === 'called' && 'bg-amber-500/5',
                    statusBadge.label === 'dialing' && 'bg-blue-500/5',
                    statusBadge.label === 'answered' && 'bg-blue-500/5',
                  )}
                >
                  <TableCell className="font-medium truncate">
                    {(contact.name ?? '') !== '' ? contact.name : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate">
                    {(contact.accountName ?? '') !== '' ? contact.accountName : '—'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground truncate">
                    {(contact.jobTitle ?? '') !== '' ? contact.jobTitle : '—'}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {(contact.phone ?? '') !== '' ? contact.phone : '—'}
                  </TableCell>
                  <TableCell className="truncate text-sm text-muted-foreground">
                    {formatNotes(t, contact.notes, contact.notesAgent, contact.notesSdr)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Badge
                      variant="outline"
                      className={cn('capitalize border text-xs px-2 py-0.5', statusBadge.className)}
                    >
                      {t(`wizard.dashboard.status.${statusBadge.label}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
